/**
 * KnowledgeCard
 *
 * Card component for displaying a domain knowledge entry in a list.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 18, 2025
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  Wrench,
  MessageSquare,
  Award,
  CheckCircle2,
  Eye,
  Pencil,
  FlaskConical,
  Clock,
  AlertTriangle,
  ChevronRight,
  Package,
} from 'lucide-react';
import { format } from 'date-fns';
import { DomainKnowledge, KnowledgeType, KnowledgeStatus, ScopeLevel } from '@/services/domainKnowledgeService';

interface KnowledgeCardProps {
  knowledge: DomainKnowledge;
  onView: (knowledge: DomainKnowledge) => void;
  onEdit?: (knowledge: DomainKnowledge) => void;
}

// Icons for knowledge types
const typeIcons: Record<KnowledgeType, React.ReactNode> = {
  terminology: <BookOpen className="h-4 w-4" />,
  specification: <Wrench className="h-4 w-4" />,
  context: <MessageSquare className="h-4 w-4" />,
  expertise_signal: <Award className="h-4 w-4" />,
  validation_rule: <CheckCircle2 className="h-4 w-4" />,
};

// Labels for knowledge types
const typeLabels: Record<KnowledgeType, string> = {
  terminology: 'Terminology',
  specification: 'Specification',
  context: 'Context',
  expertise_signal: 'Expertise Signal',
  validation_rule: 'Validation Rule',
};

// Colors for statuses with dark mode support
const statusColors: Record<KnowledgeStatus, string> = {
  proposed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  researching: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  pending_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  deprecated: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  resolved: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

// Labels for scope levels
const scopeLabels: Record<ScopeLevel, string> = {
  sector: 'Sector',
  category: 'Category',
  brand: 'Brand',
  attribute: 'Attribute',
  product: 'Product',
};

export function KnowledgeCard({ knowledge, onView, onEdit }: KnowledgeCardProps) {
  const getScopeDisplay = () => {
    switch (knowledge.scopeLevel) {
      case 'sector':
        return `Sector #${knowledge.scopeSectorId}`;
      case 'category':
        return knowledge.scopeCategoryPath;
      case 'brand':
        return knowledge.scopeBrand;
      case 'attribute':
        return knowledge.scopeAttributeName;
      case 'product':
        return `Product ${knowledge.scopeProductId}`;
      default:
        return 'Unknown';
    }
  };

  const getResearchStatusIcon = () => {
    switch (knowledge.researchStatus) {
      case 'pending':
        return <Clock className="h-3 w-3 text-gray-500" />;
      case 'in_progress':
        return <FlaskConical className="h-3 w-3 text-blue-500 animate-pulse" />;
      case 'completed':
        return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      case 'failed':
        return <AlertTriangle className="h-3 w-3 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => onView(knowledge)}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-muted">
              {typeIcons[knowledge.knowledgeType]}
            </div>
            <div>
              <CardTitle className="text-base font-medium line-clamp-1">
                {knowledge.title}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {typeLabels[knowledge.knowledgeType]} • {scopeLabels[knowledge.scopeLevel]}
              </p>
            </div>
          </div>
          <Badge className={statusColors[knowledge.status]}>
            {knowledge.status.replace('_', ' ')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {knowledge.knowledgeText || 'No content yet'}
        </p>

        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-3 text-muted-foreground">
            <span className="flex items-center gap-1">
              {getResearchStatusIcon()}
              {knowledge.researchStatus}
            </span>
            <span>
              {knowledge.createdAt ? format(new Date(knowledge.createdAt), 'MMM d, yyyy') : '-'}
            </span>
            {knowledge.effectivenessScore !== null && knowledge.effectivenessScore !== undefined && (
              <span className="flex items-center gap-1">
                Score: {(knowledge.effectivenessScore * 100).toFixed(0)}%
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(knowledge);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Scope detail */}
        <div className="mt-2 pt-2 border-t flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Scope: {getScopeDisplay()}
          </span>
          {/* Affected products badge for category-scoped entries */}
          {knowledge.scopeLevel === 'category' &&
            knowledge.affectedProductCount !== undefined &&
            knowledge.affectedProductCount > 0 && (
              <Badge
                variant="outline"
                className="text-xs flex items-center gap-1 bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800"
              >
                <Package className="h-3 w-3" />
                {knowledge.affectedProductCount} products
              </Badge>
            )}
        </div>
      </CardContent>
    </Card>
  );
}
